<script setup lang="ts">
import { computed } from "vue";
import type { Standard } from "../../types";
import Headline from "../components/Headline.vue";
import Section from "../components/Section.vue";
import { List, ListItem } from "../components/List";
import PrintPerfectCta from "../components/PrintPerfectCta.vue";
import TrimInfo from "../../TrimInfo.vue";

const props = defineProps<{
  artwork: Standard.Artwork;
  enableAbMonetizationUiV2: boolean;
  onPrintPerfectClick: () => void;
}>();

const canRemoveBg = computed(() => {
  if (props.artwork.basicPipelineData) {
    return props.artwork.basicPipelineData.canRemoveBg;
  }

  return props.artwork.canRemoveBg;
});
</script>

<template>
  <Headline v-if="enableAbMonetizationUiV2">
    <template #title>Basic enhancement</template>
    <template #subtitle>
      Kinda boring, if you ask us. Limited design tools. *Final result quality may vary.*
    </template>
  </Headline>

  <Headline v-else>
    <template #title>Basic design services.</template>
    <template #subtitle>Nothing fancy, just a solid print.</template>
  </Headline>

  <Section v-if="canRemoveBg" class="basic__trim-section">
    <TrimInfo
      v-if="!artwork.isFile && !artwork.isSimulated"
      :id="artwork.id"
      :artwork="artwork"
      :active-config-section="enableAbMonetizationUiV2 ? 'basic' : undefined"
      :can-remove-bg="canRemoveBg"
      :remove-background="artwork.removeBackground"
      :default-remove-background="!enableAbMonetizationUiV2"
      :toggle-background-error="artwork.toggleBackgroundError || ''"
      :enable-ab-monetization-ui-v2="enableAbMonetizationUiV2"
    />
  </Section>

  <Section>
    <List v-if="enableAbMonetizationUiV2" class="basic__list">
      <ListItem kind="warning">
        No
        <a href="/jiffy-transfers-FAQs#design-quality-guarantee" target="_blank">
          PrintPerfect Guarantee
        </a>
      </ListItem>
      <ListItem enable-ab-monetization-ui-v2 kind="standard">Basic background removal</ListItem>
      <ListItem enable-ab-monetization-ui-v2 kind="standard">Basic resolution improvement</ListItem>
    </List>
    <List v-else class="basic__list">
      <ListItem>Basic background removal</ListItem>
      <ListItem>Basic resolution improvement</ListItem>
      <ListItem kind="denied">
        <a href="/jiffy-transfers-FAQs#design-quality-guarantee" target="_blank">
          Design Quality Guarantee
        </a>
      </ListItem>
    </List>
    <PrintPerfectCta
      :is-ab-monetization-ui-v2="enableAbMonetizationUiV2"
      @click="onPrintPerfectClick"
    />
  </Section>
</template>

<style scoped lang="scss">
.basic {
  &__trim-section {
    margin-bottom: 8px;
  }

  &__list {
    margin-bottom: 20px;
  }
}
</style>
